import type { Getter, Setter } from 'jotai';
import type { AnnotationLabelT, AnswerT } from '../../../../../common/types/types';
import type { RelationshipStoreWithLabelDataT } from '../../../../../modernization-common/relationships';
import { questionnaireDetailsMapAtom } from '../../../../../modernization-common/side-panel/atoms/questionnaire.atom';
import type { UndoRedoActionCommand } from '../../../../../modernization-common/undo-redo/types';
import type { FrameStateT } from '../../../../labelling/types';
import type { MetadataPayload } from '../../../studio-store/types';
import { updateDynamicAnswerSegments } from '../../../studio-store/utils/dynamic-answer-utils';
import { buildDefaultDynamicAnswers } from '../../../utils/frame-timeline-v4';
import { annotationFramesAtom, annotationMetadataAtom } from '../../annotation-atoms';
import { STUDIO_WRITE_ACTIONS } from '../../studio-store-write/actions';
import { dispatchStudioStoreWriteAtom } from '../../studio-store-write/dispatch-studio-store-write';
import type { UpdateLabelForAnnotationEventPayload } from '../events';
type RemovedRelationshipsByFrameIndex = Map<number, RelationshipStoreWithLabelDataT>;
export const updateLabelForAnnotationCommandFactory = (get: Getter, set: Setter): UndoRedoActionCommand<UpdateLabelForAnnotationEventPayload, void> => {
  let previousAnnotationLabel: AnnotationLabelT | undefined;
  let previousAnswers: AnswerT | undefined;
  let previousDynamicAnswers: MetadataPayload['dynamicAnswers'];
  let removedRelationshipsByFrameIndex: RemovedRelationshipsByFrameIndex = new Map();
  return {
    execute: payload => {
      const {
        annotationIdx,
        annotationLabel
      } = payload;
      const frames = [...get(annotationFramesAtom)];
      const frameWithAnnotationIdx = frames.find(frame => !!frame.annotationStore[annotationIdx]);
      if (!frameWithAnnotationIdx) {
        return;
      }
      const previousAnnotation = frameWithAnnotationIdx.annotationStore[annotationIdx];
      const priorAnnotationLabel = previousAnnotation?.annotationLabel;
      if (!priorAnnotationLabel || priorAnnotationLabel.title === annotationLabel.title) {
        return;
      }
      previousAnnotationLabel = priorAnnotationLabel;
      previousAnswers = previousAnnotation?.metadata?.answers;
      previousDynamicAnswers = get(annotationMetadataAtom).get(annotationIdx)?.dynamicAnswers;
      const {
        updatedFrames,
        removedRelationshipsByFrameIndex: removed
      } = applyLabelChangeReset(frames, annotationIdx, annotationLabel);
      removedRelationshipsByFrameIndex = removed;
      set(annotationFramesAtom, updatedFrames);
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
        annotationId: annotationIdx,
        partialPayload: {
          labelId: annotationLabel.title,
          answers: undefined,
          dynamicAnswers: undefined
        }
      });
      const questionnaireId = annotationLabel.questionnaire?.[0]?.questionnaireId;
      const cachedQuestionnaire = questionnaireId ? get(questionnaireDetailsMapAtom)[questionnaireId] : undefined;
      const defaultAnswers = cachedQuestionnaire?.status === 'success' ? buildDefaultDynamicAnswers(cachedQuestionnaire.questionnaire) : undefined;
      const spans = get(annotationMetadataAtom).get(annotationIdx)?.spans ?? [];
      if (defaultAnswers && spans.length > 0) {
        const dynamicAnswers = spans.reduce((segments, span) => updateDynamicAnswerSegments(segments, {
          frameSpan: span,
          answers: defaultAnswers
        }), ([] as ReturnType<typeof updateDynamicAnswerSegments>));
        set(dispatchStudioStoreWriteAtom, {
          type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
          annotationId: annotationIdx,
          partialPayload: {
            dynamicAnswers
          }
        });
      }
    },
    undo: payload => {
      const {
        annotationIdx
      } = payload;
      if (!previousAnnotationLabel) {
        return;
      }
      const frames = [...get(annotationFramesAtom)];
      const restoredFrames = restoreLabelChange(frames, annotationIdx, previousAnnotationLabel, previousAnswers, previousDynamicAnswers, removedRelationshipsByFrameIndex);
      set(annotationFramesAtom, restoredFrames);
      set(dispatchStudioStoreWriteAtom, {
        type: STUDIO_WRITE_ACTIONS.UPDATE_METADATA,
        annotationId: annotationIdx,
        partialPayload: {
          labelId: previousAnnotationLabel.title,
          answers: previousAnswers,
          dynamicAnswers: previousDynamicAnswers
        }
      });
    }
  };
};
const applyLabelChangeReset = (frames: FrameStateT[], annotationIdx: string, annotationLabel: AnnotationLabelT): {
  updatedFrames: FrameStateT[];
  removedRelationshipsByFrameIndex: RemovedRelationshipsByFrameIndex;
} => {
  const removedRelationshipsByFrameIndex: RemovedRelationshipsByFrameIndex = new Map();
  const updatedFrames = frames.map((frame, frameIndex) => {
    if (!frame.annotationStore[annotationIdx]) {
      return frame;
    }
    let filteredRelationshipStore = frame.relationshipStore;
    if (frame.relationshipStore) {
      const removedEntries = Object.entries(frame.relationshipStore).filter(([, rel]) => rel.sourceAnnotationId === annotationIdx || rel.targetAnnotationId === annotationIdx);
      if (removedEntries.length > 0) {
        removedRelationshipsByFrameIndex.set(frameIndex, Object.fromEntries(removedEntries));
        filteredRelationshipStore = Object.fromEntries(Object.entries(frame.relationshipStore).filter(([, rel]) => rel.sourceAnnotationId !== annotationIdx && rel.targetAnnotationId !== annotationIdx));
      }
    }
    return {
      ...frame,
      annotationStore: {
        ...frame.annotationStore,
        [annotationIdx]: {
          ...frame.annotationStore[annotationIdx],
          annotationLabel,
          metadata: {
            ...frame.annotationStore[annotationIdx]?.metadata,
            answers: undefined,
            dynamicAnswers: undefined
          }
        }
      },
      relationshipStore: filteredRelationshipStore
    };
  });
  return {
    updatedFrames,
    removedRelationshipsByFrameIndex
  };
};
const restoreLabelChange = (frames: FrameStateT[], annotationIdx: string, annotationLabel: AnnotationLabelT, answers: AnswerT | undefined, dynamicAnswers: MetadataPayload['dynamicAnswers'], removedRelationshipsByFrameIndex: RemovedRelationshipsByFrameIndex): FrameStateT[] => {
  return frames.map((frame, frameIndex) => {
    if (!frame.annotationStore[annotationIdx]) {
      return frame;
    }
    const restoredRelationships = removedRelationshipsByFrameIndex.get(frameIndex);
    const relationshipStore = restoredRelationships ? {
      ...frame.relationshipStore,
      ...restoredRelationships
    } : frame.relationshipStore;
    return {
      ...frame,
      annotationStore: {
        ...frame.annotationStore,
        [annotationIdx]: {
          ...frame.annotationStore[annotationIdx],
          annotationLabel,
          metadata: {
            ...frame.annotationStore[annotationIdx]?.metadata,
            answers,
            dynamicAnswers
          }
        }
      },
      relationshipStore
    };
  });
};